/**
 * WizardStepTrades (Step 2)
 *
 * Trade selection - what type of work will be performed.
 * Users can select multiple trades for multi-trade projects.
 */

import { useCallback, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import {
  Zap,
  Droplets,
  Wind,
  Home,
  Flame,
  Shield,
  Building,
  Wrench,
} from 'lucide-react';
import type { WizardTradesData, ContractorRole } from '@/types/wizard';
import type { TradeType } from '@/integrations/supabase/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface WizardStepTradesProps {
  data: WizardTradesData;
  onUpdate: (data: Partial<WizardTradesData>) => void;
  tradeTypes: TradeType[];
  contractorRole?: ContractorRole;
}

// Icon mapping for trade types
const tradeIcons: Record<string, React.ElementType> = {
  electrical: Zap,
  plumbing: Droplets,
  mechanical: Wind,
  hvac: Wind,
  roofing: Home,
  building: Building,
  gas: Flame,
  fire_alarm: Shield,
  general: Wrench,
};

// Default icon
const DefaultIcon = Wrench;

export function WizardStepTrades({ data, onUpdate, tradeTypes, contractorRole }: WizardStepTradesProps) {
  // Handle trade selection toggle
  const handleTradeToggle = useCallback((trade: TradeType, checked: boolean) => {
    let newSelectedIds: number[];
    let newTrades: WizardTradesData['trades'];

    if (checked) {
      newSelectedIds = [...data.selectedTradeIds, trade.id];
      newTrades = [...data.trades, { id: trade.id, code: trade.code, name: trade.name }];
    } else {
      newSelectedIds = data.selectedTradeIds.filter((id) => id !== trade.id);
      newTrades = data.trades.filter((t) => t.id !== trade.id);
    }

    onUpdate({
      selectedTradeIds: newSelectedIds,
      trades: newTrades,
    });
  }, [data, onUpdate]);

  // Track if we've already auto-selected based on contractor role
  const hasAutoSelected = useRef(false);

  // Auto-select Building/General trade for GC roles and Owner-Builder
  useEffect(() => {
    // Only auto-select once, and only when we have trade types loaded
    if (hasAutoSelected.current || tradeTypes.length === 0) return;

    // Roles that require Building permit
    const rolesRequiringBuilding = ['gc_all_permits', 'gc_building_only', 'owner_builder'];

    if (contractorRole && rolesRequiringBuilding.includes(contractorRole)) {
      // Find the Building/General trade
      const generalTrade = tradeTypes.find(t => t.code === 'general');

      if (generalTrade && !data.selectedTradeIds.includes(generalTrade.id)) {
        // Auto-select it
        onUpdate({
          selectedTradeIds: [...data.selectedTradeIds, generalTrade.id],
          trades: [...data.trades, { id: generalTrade.id, code: generalTrade.code, name: generalTrade.name }],
        });
        hasAutoSelected.current = true;
      }
    }
  }, [contractorRole, tradeTypes, data.selectedTradeIds, data.trades, onUpdate]);

  if (tradeTypes.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label className="text-base">
          What work will be performed?
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <p className="text-sm text-muted-foreground">
          Select all trades that apply. For multi-trade projects, each trade may need a separate permit.
        </p>
      </div>

      <div className="grid gap-3">
        {tradeTypes.map((trade) => {
          const isSelected = data.selectedTradeIds.includes(trade.id);
          const Icon = tradeIcons[trade.code] || DefaultIcon;

          // Check if this trade is auto-required based on contractor role
          const rolesRequiringBuilding = ['gc_all_permits', 'gc_building_only', 'owner_builder'];
          const isAutoRequired = trade.code === 'general' &&
            contractorRole &&
            rolesRequiringBuilding.includes(contractorRole);

          return (
            <Card
              key={trade.id}
              className={`cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'hover:border-muted-foreground/50'
              }`}
              onClick={() => handleTradeToggle(trade, !isSelected)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => handleTradeToggle(trade, checked === true)}
                    className="mt-1 h-5 w-5 rounded-[3px] border-2"
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{trade.name}</span>
                      {isSelected && isAutoRequired && (
                        <Badge
                          variant="outline"
                          className="text-xs border-muted-foreground/30 bg-muted/50 text-foreground"
                        >
                          Required for your role
                        </Badge>
                      )}
                      {isSelected && !isAutoRequired && (
                        <Badge variant="secondary" className="text-xs">
                          Selected
                        </Badge>
                      )}
                    </div>
                    {trade.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {trade.description}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Contractor role guidance */}
      {contractorRole && data.trades.some(t => t.code === 'general') && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {contractorRole === 'gc_all_permits' && (
              <span>
                As a <strong>GC pulling all permits</strong>, you'll pull the Building (Master) permit
                and any sub-permits for the trades selected below.
              </span>
            )}
            {contractorRole === 'gc_building_only' && (
              <span>
                As a <strong>GC pulling Building only</strong>, you'll pull the Building permit.
                Your licensed subcontractors will pull their own trade permits.
              </span>
            )}
            {contractorRole === 'owner_builder' && (
              <span>
                As an <strong>Owner-Builder</strong>, you can pull the Building permit yourself.
                However, <strong>Electrical, Plumbing, HVAC, and Gas</strong> permits must be pulled by licensed contractors.
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Selection summary */}
      {data.selectedTradeIds.length > 0 && (
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm font-medium">
            {data.selectedTradeIds.length} trade{data.selectedTradeIds.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.trades.map((trade) => (
              <Badge key={trade.id} variant="secondary">
                {trade.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {data.selectedTradeIds.length === 0 && (
        <p className="text-sm text-red-500 dark:text-red-400 flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" />
          At least one trade is required
        </p>
      )}
    </div>
  );
}
